const MB = 1_048_576

export const EMAIL_MAX_FILES = 10
export const EMAIL_MAX_TOTAL_BYTES = 10 * MB
export const EMAIL_SUBJECT_MAX = 200

const TYPE_BY_EXTENSION: Record<string, string> = {
  pdf: `application/pdf`,
  png: `image/png`,
  jpg: `image/jpeg`,
  jpeg: `image/jpeg`,
  gif: `image/gif`,
  heic: `image/heic`,
  heif: `image/heif`,
  doc: `application/msword`,
  docx: `application/vnd.openxmlformats-officedocument.wordprocessingml.document`,
  xls: `application/vnd.ms-excel`,
  xlsx: `application/vnd.openxmlformats-officedocument.spreadsheetml.sheet`,
}
const ALLOWED_TYPES = new Set(Object.values(TYPE_BY_EXTENSION))

export const EMAIL_ACCEPT = Object.keys(TYPE_BY_EXTENSION)
  .map((extension) => `.${extension}`)
  .join(`,`)

const REFUSAL_COPY: Record<string, string> = {
  EMAIL_OPTED_OUT: `This contact has opted out of email.`,
  WORKSPACE_SENDING_PAUSED: `Sending is paused for this workspace.`,
  LOCATION_NOT_CONFIGURED: `This location's email settings aren't set up yet.`,
  DOMAIN_NOT_VERIFIED: `This location's sending domain isn't verified yet.`,
  INVALID_RECIPIENT: `The To address isn't a valid email address.`,
  MISSING_CONTENT: `Add a subject and a message.`,
  CONTENT_TOO_LARGE: `The subject or message is too long.`,
  ATTACHMENT_INVALID: `One of the files couldn't be read. Remove it and attach it again.`,
  ATTACHMENT_TYPE_NOT_ALLOWED: `One of the files is a type that can't be sent.`,
  ATTACHMENTS_TOO_LARGE: `The files are over 10 files or 10 MB in total.`,
  ATTACHMENT_UPLOAD_FAILED: `The files didn't upload. Try sending again.`,
}

export const EMAIL_NETWORK_FAILURE = `The connection dropped before the email was sent.`
export const EMAIL_NO_CONTACT = `This conversation isn't linked to a contact yet. Link one to email them.`

export function resolveEmailContentType(file: { name: string; type: string }): string | undefined {
  if (file.type && file.type !== `application/octet-stream`) return file.type
  return TYPE_BY_EXTENSION[file.name.split(`.`).pop()?.toLowerCase() ?? ``]
}

export function attachmentError(
  file: { name: string; type: string; size: number },
  accepted: { count: number; bytes: number },
): string | undefined {
  const contentType = resolveEmailContentType(file)
  if (!contentType || !ALLOWED_TYPES.has(contentType)) {
    return `This file type can't be sent. Use PDF, images, Word or Excel.`
  }
  if (file.size === 0) return `This file is empty.`
  if (accepted.count >= EMAIL_MAX_FILES) return `An email can carry up to ${EMAIL_MAX_FILES} files.`
  if (accepted.bytes + file.size > EMAIL_MAX_TOTAL_BYTES) return `This file puts the email over 10 MB in total.`
  return undefined
}

export function emailRefusalReason(code: string | undefined, fallback: string | undefined): string {
  return (code && REFUSAL_COPY[code]) || fallback || `The email wasn't sent.`
}

export function formatFileSize(bytes: number): string {
  if (bytes < MB) return `${Math.max(1, Math.round(bytes / 1024))} KB`
  return `${(bytes / MB).toFixed(1)} MB`
}
